import React from "react";

  // SHIMMER_KEYFRAMES_INJECTED
  if (typeof document !== "undefined" && !document.getElementById("policy-shimmer-keyframes")) {
    const styleEl = document.createElement("style");
    styleEl.id = "policy-shimmer-keyframes";
    styleEl.textContent = "@keyframes policyShimmer { 100% { transform: translateX(100%); } }";
    document.head.appendChild(styleEl);
  }
  
const Shimmer = ({ className = "" }) => (
  <div
    className={`relative overflow-hidden bg-[#E5E9EB] rounded-md before:absolute before:inset-0 before:-translate-x-full before:animate-[policyShimmer_1.4s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/70 before:to-transparent ${className}`}
    aria-hidden="true"
  />
);

const BenefitRowSkeleton = () => (
  <div className="flex items-start gap-3">
    <Shimmer className="w-4 h-4 rounded-full mt-1 flex-shrink-0" />
    <Shimmer className="h-3.5 w-[80%]" />
  </div>
);

const InfoRowSkeleton = () => (
  <div className="flex items-center justify-between py-3 border-b border-[#EEF1F3] last:border-b-0">
    <Shimmer className="h-3.5 w-24" />
    <Shimmer className="h-3.5 w-32" />
  </div>
);

const NonMotorPolicyDetailsSkeleton = ({ partialData }) => {
  const hasPartial = !!partialData;

  return (    <div className="p-6 max767:p-0" data-testid="non-motor-policy-skeleton">
      {/* Desktop breadcrumb */}
      <div className="max767:hidden mb-4">
        <div className="flex items-center gap-2">
          <Shimmer className="h-3 w-20" />
          <Shimmer className="h-3 w-3 rounded-full" />
          <Shimmer className="h-3 w-32" />
        </div>
      </div>

      {/* Mobile sticky top bar (matches NonMotorPolicyCard mobile header) */}
      <div className="hidden max767:flex items-center justify-between bg-[#EDFAFF] p-4 sticky top-0 z-10">
        <div className="flex items-center gap-2">
          <Shimmer className="w-5 h-5 rounded-full" />
          <Shimmer className="h-4 w-20 rounded-3xl" />
        </div>
        <Shimmer className="h-4 w-20" />
      </div>

      {/* NonMotorPolicyCard skeleton */}
      <div className="bg-white border border-[#DDE2E4] rounded-2xl p-6 mb-6 max767:mx-4 max767:mt-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {hasPartial && partialData?.PolicyNumber ? (
              <p className="text-[#1A2024] text-xl font-semibold mb-2 truncate">
                {partialData.PolicyNumber}
              </p>
            ) : (
              <Shimmer className="h-5 w-48 mb-3" />
            )}
            <Shimmer className="h-3 w-32 mb-4" />

            <div className="flex items-center gap-6 mt-4 max767:flex-col max767:items-start max767:gap-3">
              <div>
                <Shimmer className="h-3 w-16 mb-2" />
                {hasPartial && partialData?.FromDateStr ? (
                  <p className="text-[#1A2024] text-sm font-medium">
                    {partialData.FromDateStr}
                  </p>
                ) : (
                  <Shimmer className="h-4 w-24" />
                )}
              </div>
              <div>
                <Shimmer className="h-3 w-16 mb-2" />
                {hasPartial && partialData?.ToDateStr ? (
                  <p className="text-[#1A2024] text-sm font-medium">
                    {partialData.ToDateStr}
                  </p>
                ) : (
                  <Shimmer className="h-4 w-24" />
                )}
              </div>
            </div>
          </div>
          <Shimmer className="w-9 h-9 rounded-full max767:hidden" />
        </div>
      </div>

      {/* Tabs strip skeleton */}
      <div className="flex items-center gap-6 border-b border-[#EEF1F3] mb-6 max767:px-4">
        <div className="pb-3 border-b-2 border-[#005574]">
          <Shimmer className="h-4 w-28" />
        </div>
        <div className="pb-3">
          <Shimmer className="h-4 w-28 opacity-60" />
        </div>
      </div>

      {/* PolicyOverviewTab content skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 max767:px-4">
        {/* Left: plan / benefits */}
        <div className="lg:col-span-3 bg-white border border-[#DDE2E4] rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Shimmer className="w-12 h-12 rounded-full" />
            <Shimmer className="h-6 w-56" />
          </div>

          {/* Mobile collapsible header */}
          <div className="max767:flex hidden items-center gap-2 mb-3">
            <Shimmer className="w-4 h-4" />
            <Shimmer className="h-4 w-36" />
          </div>
          {/* Desktop benefits header */}
          <div className="max767:hidden mb-4">
            <Shimmer className="h-5 w-40" />
          </div>

          {/* Info rows / benefit rows */}
          <div className="space-y-3">
            <BenefitRowSkeleton />
            <BenefitRowSkeleton />
            <BenefitRowSkeleton />
            <BenefitRowSkeleton />
            <BenefitRowSkeleton />
          </div>
        </div>

        {/* Right: actions + assistance */}
        <div className="lg:col-span-2 flex flex-col gap-6">
          {/* File a Claim */}
          <div className="bg-[#F6F8F9] border border-[#DDE2E4] rounded-2xl p-6">
            <div className="flex items-center gap-3">
              <Shimmer className="w-9 h-9 rounded-full" />
              <Shimmer className="h-5 w-32" />
            </div>
          </div>

          {/* Assistance */}
          <div className="bg-[#F6F8F9] rounded-2xl p-6">
            <Shimmer className="h-5 w-40 mb-4" />
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
              <div className="flex items-center gap-3">
                <Shimmer className="w-10 h-10 rounded-full" />
                <Shimmer className="h-4 w-24" />
              </div>
              <div className="flex items-center gap-3">
                <Shimmer className="w-10 h-10 rounded-full" />
                <Shimmer className="h-4 w-24" />
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="bg-[#F6F8F9] rounded-2xl p-6">
            <Shimmer className="h-4 w-48 mb-4" />
            <div className="space-y-3">
              <InfoRowSkeleton />
              <InfoRowSkeleton />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NonMotorPolicyDetailsSkeleton;
